<template>
  <content-loading v-if="isLoading" />
  <div v-else-if="questionSet">
    <div class="d-flex justify-content-between" style="margin-bottom: 1.70rem;">
      <div class="d-flex">
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="38" viewBox="0 0 40 38" fill="none" style="cursor: pointer;" @click="goBack()">
          <path
            d="M38.75 19.0003H1.25M1.25 19.0003L18.9583 1.29199M1.25 19.0003L18.9583 36.7087"
            stroke="#FF7C48"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <div class="d-flex align-items-center" style="margin-left: 1.25rem; gap: 2.34rem;">
          <div>
            <h1 class="title">{{ $t("question.answering") }}</h1>
          </div>
        </div>
      </div>
      <div class="d-flex align-items-start flex-wrap top-meta">
        <div class="d-flex align-items-center flex-wrap ">
          <div class="icon"><i class="iconoir-chat-bubble-question"></i></div>
          <div class="itext">{{ $t("question-set.answered-question-count") }}:</div>
          <div class="inumber mr-3">{{ questionSet.answeredQuestionCount }}/{{ questionSet.questionCount }}</div>

          <div class="icon"><i class="iconoir-medal"></i></div>
          <div class="itext">{{ $t("common.point") }}:</div>
          <div class="inumber mr-3">{{ questionSet.score }}</div>

          <div class="d-flex align-items-center" v-if="canAssign">
            <div class="assignee-wrapper mt-2">
              <organization-member-select :selected="questionSet.assignee" @onSelect="onAssigneeChanged($event, questionSet)" style="min-width: 250px;" />

              <i
                v-if="questionSet.assignee"
                class="bx bx-x hand"
                @click="onAssigneeChanged(undefined, questionSet)"
                v-b-tooltip.hover.top="{ variant: 'main' }"
                :title="$t('question.assignee.remove')"
                style="font-size: 2rem; color: #C3CCE7;"
              ></i>

              <i class="iconoir-info-circle" v-b-tooltip.hover.top="{ variant: 'main' }" :title="$t('question.assignee.text')" style="font-size: 1.125rem; color: #C3CCE7;"></i>
            </div>
          </div>
        </div>

        <div v-if="isFirmQuestionSet" class="ml-auto d-flex flex-column align-items-end status-pill mt-3 mt-md-0">
          <b-badge :variant="statusVariant" class="mb-1">{{ currentStatusLabel }}</b-badge>
          <small class="text-muted" v-if="firmName"
            ><strong>{{ $t("firm.audit.firm-name") }}:</strong> {{ firmName }}</small
          >
          <small class="text-muted" v-if="assigneeName"
            ><strong>{{ $t("firm.audit.assignee") }}:</strong> {{ assigneeName }}</small
          >
          <small class="text-muted" v-if="sourceQuestionSetName"
            ><strong>{{ $t("firm.audit.source-question-set") }}:</strong> {{ sourceQuestionSetName }}</small
          >
        </div>
      </div>
    </div>
    <div v-if="isFirmQuestionSet" class="timeline-container my-3">
      <div class="timeline-row timeline-row--labels">
        <div class="timeline-cell" v-for="item in timelineItems" :key="item.key">
          <div class="timeline-label">{{ item.label }}</div>
        </div>
      </div>
      <div class="timeline-row timeline-row--values">
        <div class="timeline-cell" v-for="item in timelineItems" :key="item.key + '-value'">
          <div class="timeline-value">{{ item.value | date(true) }}</div>
        </div>
      </div>
    </div>
    <!-- <b-alert v-if="submitWarningText" show variant="warning" class="mb-3">{{ submitWarningText }}</b-alert> -->
    <div class="acard">
      <div class="qtitle mb-4">{{ questionSet.name | translate }}</div>

      <h6>{{ $t("common.description") }}</h6>
      <div class="atext">
        {{ questionSet.description | translate }}
      </div>
    </div>

    <div v-if="unansweredQuestionOrders.length > 0" class="unanswered-bar mb-4">
      <div class="d-flex align-items-center flex-wrap">
        <div class="unanswered-label mr-3">
          <i class="iconoir-warning-triangle text-warning mr-2"></i>
          {{ $t("question-set.unanswered-questions") }}:
        </div>
        <div class="unanswered-list d-flex flex-wrap" style="gap: 0.5rem;">
          <div v-for="order in unansweredQuestionOrders" :key="'unanswered-' + order" class="unanswered-badge cursor-pointer" @click="scrollToQuestion(order)">
            <span class="hand"> {{ order }}</span>
          </div>
        </div>
      </div>
    </div>

    <template v-for="question in questionSet.questions">
      <div :key="question._id + '1'" v-if="question.canDisplay" :id="'question-' + question.order">
        <div class="question">
          <div class="question-sidebar mr-3">
            <div class="number answered">{{ question.order }}</div>
            <a href="javascript:;" class="back-to-top-link mt-2" @click="scrollToTop" v-b-tooltip.hover.right="{ variant: 'main' }" :title="$t('common.back-to-top')">
              <i class="bx bx-up-arrow-alt font-size-20"></i>
            </a>
          </div>
          <div class="question-body">
            <div class="text">
              {{ question.name | translate }}
              <b-badge v-if="question.fileRequired" variant="danger" class="ml-2 align-middle">
                <strong> {{ $t("firm.audit.question.required-attachment") }}</strong>
              </b-badge>
              <div class="text-muted font-size-12">
                <i v-if="question.answerType == 'MULTIPLE'">{{ $t("question.answer-type.multiple") }}</i>
                <i v-if="question.answerType == 'SINGLE'">{{ $t("question.answer-type.single") }}</i>
                <!-- <i v-if="question.answerType == 'FREE_TEXT'">{{ $t("question.answer-type.free-text") }}</i> -->
              </div>
            </div>

            <div class="mt-2" v-if="question.fwsedCategory && currentAccountHasPermissions(['ORGANIZATION_INFORMATION_TECHNOLOGY_AUDITOR'])">
              <question-category-select
                :label="$t('question.category.fwsed')"
                v-if="questionSet.products.length > 0 && questionSet.products.includes('FWSED')"
                class="mt-2"
                :selected="question.fwsedCategory"
                :disabled="true"
              />
              <!-- <span>{{ $t("common.category") }}: {{ $t("firm.status." + question.fwsedCategory.toLowerCase().replace("_", "-")) }} </span> -->
            </div>
            <div class="mt-2" v-if="question.reviewStatus">
              <b-badge :variant="reviewStatusVariant(question.reviewStatus)">{{ reviewStatusLabel(question.reviewStatus) }}</b-badge>
              <small class="text-muted ml-2" v-if="question.reviewedBy">
                {{ $t("firm.audit.review.reviewed-by", { name: question.reviewedBy.name }) }}
              </small>
              <small class="text-muted ml-2" v-if="question.reviewedAt">{{ question.reviewedAt | date(true) }}</small>
            </div>
            <div v-if="question.reviewComment" class="bt-comment">
              <strong>{{ $t("firm.audit.review.bt-comment") }}:</strong> {{ question.reviewComment }}
            </div>
            <div v-if="canReview && question.reportNote" class="bt-comment text-muted">
              <strong>{{ $t("firm.audit.review.report-note") }}:</strong> {{ question.reportNote }}
            </div>
            <div v-if="question.answerType != 'FREE_TEXT'" class="answers" :class="{ disabled: !canEditAnswers(question) }">
              <label
                v-for="option in question.options"
                :key="question._id + option._id"
                @click="selectOption(question, option)"
                class="answer"
                :class="{ active: isOptionSelected(question, option), disabled: !canEditAnswers(question) }"
              >
                <div class="custom-radio"><i class="iconoir-check"></i></div>
                {{ option.name | translate }}
              </label>
            </div>
            <div v-else>
              <textarea
                :value="question.freeTextAnswer"
                @input="updateAnswer($event, question)"
                class="mt-3 text-dark w-100"
                rows="5"
                cols="100"
                :placeholder="$t('question-set.answer')"
                :disabled="!canEditAnswers(question)"
              ></textarea>
            </div>
            <textarea
              v-if="question.answerType != 'FREE_TEXT'"
              :value="question.note && question.note.content"
              @input="updateNote($event, question)"
              class="mt-3 w-100"
              cols="53"
              rows="3"
              :placeholder="$t('common.note')"
              :disabled="!canEditAnswers(question)"
            ></textarea>

            <!-- <div class="mt-3">
              <h6>{{ $t("audit.related-policy") }}</h6>
              <ul>
                <li v-for="policy in question.policies" :key="policy._id">{{ policy.name | translate }}</li>
              </ul>
            </div> -->

            <div v-if="canReview && question.policies && question.policies.length" class="history-wrapper">
              <b-button size="sm" variant="outline-secondary" class="mb-2" @click="toggleRelatedPolicies(question, 'policies')">
                {{ isRelatedPoliciesOpen(question, "reviews") ? $t("firm.audit.related-policies.hide") : $t("firm.audit.related-policies.show") }}
              </b-button>
              <b-collapse :visible="isRelatedPoliciesOpen(question, 'policies')">
                <div class="table-responsive">
                  <table class="table table-sm table-bordered history-table">
                    <tr v-for="entry in question.policies" :key="question._id + '-' + '-' + entry._id">
                      <td>{{ entry.name | translate }}</td>
                    </tr>
                  </table>
                </div>
              </b-collapse>
            </div>

            <i v-if="question.note" class="text-muted">
              {{ question.note.account.name }}
            </i>

            <div class="question-files mt-3">
              <div class="d-flex align-items-center flex-wrap mb-2">
                <h6 class="mb-2 mb-md-0">
                  {{ $t("firm.audit.question.attachments") }}
                  <span v-if="question.fileRequired" class="text-danger">*</span>
                </h6>
                <div v-if="canEditAnswers(question)" class="ml-md-auto">
                  <file-rocket
                    :allowedFormats="allowedFormats"
                    :maxFileSize="600000000000"
                    :maxFileLength="3"
                    :multiple="true"
                    :showProgress="'Upload'"
                    :targetUrl="`question/${question._id}/upload`"
                    @response="onQuestionUpload(question, $event)"
                  />
                </div>
              </div>
              <div v-if="question.files && question.files.length" class="question-file-grid">
                <div class="question-file-card" v-for="file in question.files" :key="question._id + '-file-' + file._id">
                  <div class="d-flex align-items-start">
                    <file-icon class="mr-3" :extension="file.extension" />
                    <div class="flex-grow-1">
                      <div class="font-weight-600" v-b-tooltip.hover :title="$options.filters.translate(file.name)">
                        {{ file.name | translate | summary(30) }}
                      </div>
                      <div class="small text-muted">{{ file.size | formatByte }}</div>
                      <div class="small text-muted" v-if="file.uploader">{{ file.uploader.name }}</div>
                      <div class="small text-muted" v-if="file.uploadedAt">{{ file.uploadedAt | date(true) }}</div>
                    </div>
                  </div>
                  <div class="question-file-actions mt-2">
                    <i @click="downloadFile(file)" v-b-tooltip.hover :title="$t('file-manager.download')" class="bx bxs-download text-dark hand mr-2"></i>
                    <i v-if="canEditAnswers(question)" @click="deleteFile(question, file)" v-b-tooltip.hover :title="$t('common.delete')" class="bx bx-trash text-danger hand"></i>
                  </div>
                </div>
              </div>
              <div v-else>
                <p class="text-muted small mb-0">{{ $t("firm.audit.question.no-attachments") }}</p>
                <h6 v-if="question.fileRequired" class="text-danger mb-0">
                  <strong>{{ $t("firm.audit.question.required-attachment-hint") }}</strong>
                </h6>
              </div>
            </div>

            <div v-if="canReview" class="review-panel mt-3">
              <div class="btn-group mb-2" role="group">
                <b-button
                  size="sm"
                  :variant="reviewDraft(question).reviewStatus === 'APPROVED' ? 'success' : 'outline-success'"
                  @click="setReviewStatus(question, 'APPROVED')"
                  :disabled="!canEditReviews || isApproveDisabled(question)"
                >
                  {{ $t("firm.audit.review.approve") }}
                </b-button>
                <b-button
                  size="sm"
                  :variant="reviewDraft(question).reviewStatus === 'REVISION_REQUIRED' ? 'danger' : 'outline-danger'"
                  @click="setReviewStatus(question, 'REVISION_REQUIRED')"
                  :disabled="!canEditReviews"
                >
                  {{ $t("firm.audit.review.revision") }}
                </b-button>
                <b-button
                  size="sm"
                  :variant="reviewDraft(question).reviewStatus === 'PENDING' ? 'warning' : 'outline-warning'"
                  @click="setReviewStatus(question, 'PENDING')"
                  :disabled="!canEditReviews"
                >
                  {{ $t("firm.audit.review.pending") }}
                </b-button>
              </div>
              <sweet-textarea
                :rows="3"
                :validate="false"
                class="mb-2"
                :placeholder="$t('firm.audit.review.comment-placeholder')"
                v-model="reviewDraft(question).reviewComment"
                :disabled="!canEditReviews || !['REVISION_REQUIRED', 'PENDING'].includes(reviewDraft(question).reviewStatus)"
              />

              <sweet-textarea
                :rows="2"
                :validate="false"
                class="mb-2"
                :placeholder="$t('firm.audit.review.report-placeholder')"
                v-model="reviewDraft(question).reportNote"
                :disabled="!canEditReviews"
              />
            </div>

            <div v-if="canViewHistory && question.history && question.history.length" class="history-wrapper">
              <b-button size="sm" variant="outline-secondary" class="mb-2" @click="toggleHistory(question, 'answers')">
                {{ isHistoryOpen(question, "answers") ? $t("firm.audit.history.hide") : $t("firm.audit.history.show") }}
              </b-button>
              <b-collapse :visible="isHistoryOpen(question, 'answers')">
                <div class="table-responsive">
                  <table class="table table-sm table-bordered history-table">
                    <thead>
                      <tr>
                        <th>{{ $t("firm.audit.history.timestamp") }}</th>
                        <th>{{ $t("firm.audit.history.answered-by") }}</th>
                        <th>{{ $t("firm.audit.history.answers") }}</th>
                        <th>{{ $t("firm.audit.history.free-text2") }}</th>
                        <th>{{ $t("firm.audit.history.note") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in question.history" :key="question._id + '-' + item.sessionId + '-' + item.at">
                        <td>{{ item.at | date(true) }}</td>
                        <td>{{ item.account ? item.account.name : "" }}</td>
                        <td>
                          <span v-if="item.answers && item.answers.length">
                            <ul class="mb-0 pl-3">
                              <li v-for="ans in item.answers" :key="question._id + '-' + item.sessionId + '-' + ans.optionId">{{ ans.name | translate }}</li>
                            </ul>
                          </span>
                          <span v-else>-</span>
                        </td>
                        <td>{{ item.freeTextAnswer || "-" }}</td>
                        <td>{{ item.note || "-" }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-collapse>
            </div>

            <div v-if="canViewHistory && question.reviewHistory && question.reviewHistory.length" class="history-wrapper">
              <b-button size="sm" variant="outline-secondary" class="mb-2" @click="toggleHistory(question, 'reviews')">
                {{ isHistoryOpen(question, "reviews") ? $t("firm.audit.history.hide-review") : $t("firm.audit.history.show-review") }}
              </b-button>
              <b-collapse :visible="isHistoryOpen(question, 'reviews')">
                <div class="table-responsive">
                  <table class="table table-sm table-bordered history-table">
                    <thead>
                      <tr>
                        <th>{{ $t("firm.audit.history.timestamp") }}</th>
                        <th>{{ $t("firm.audit.history.reviewed-by") }}</th>
                        <th>{{ $t("firm.audit.history.status") }}</th>
                        <th>{{ $t("firm.audit.history.comment") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="entry in question.reviewHistory" :key="question._id + '-' + entry.status + '-' + entry.at">
                        <td>{{ entry.at | date(true) }}</td>
                        <td>{{ entry.account ? entry.account.name : "" }}</td>
                        <td>{{ reviewStatusLabel(entry.status) }}</td>
                        <td>{{ entry.comment || "-" }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-collapse>
            </div>
          </div>
        </div>
        <hr />
      </div>
    </template>

    <div class="mt-5 d-flex flex-wrap align-items-center">
      <button class="btn btn-outline-secondary mr-3" @click="goBack()">{{ $t("common.go-to-back") }}</button>
      <button class="btn btn-soft-primary mr-3" @click="scrollToTop()"><i class="bx bx-up-arrow-alt mr-1"></i> {{ $t("common.back-to-top") }}</button>
      <b-button v-if="canSubmitToBt" variant="primary" class="btn btn-main" :disabled="isSubmitting" @click="submitForBt">
        <b-spinner v-if="isSubmitting" small type="border" class="mr-2" />
        {{ $t("firm.audit.submit-to-bt") }}
      </b-button>
      <!-- <button v-if="!canSubmitToBt" class="btn btn-success" @click="goBack()">{{ $t("common.ok") }}</button> -->
      <b-button
        v-if="canReview"
        variant="primary"
        class="btn btn-main ml-auto mt-3 mt-md-0"
        :disabled="!canEditReviews || !hasReviewChanges || isSavingReviews"
        @click="saveAllReviews"
      >
        <b-spinner v-if="isSavingReviews" small type="border" class="mr-2" />
        {{ $t("firm.audit.review.save-all") }}
      </b-button>
    </div>
  </div>
</template>

<script>
import FileRocket from "@c/form/file-rocket";
import SweetTextarea from "@c/form/sweet-textarea";
import OrganizationMemberSelect from "@cc/organization-member-select";
import QuestionCategorySelect from "@cc/question-category-select.vue";
import FileIcon from "@modules/file-manager/file-icon.vue";

export default {
  components: { OrganizationMemberSelect, FileRocket, FileIcon, SweetTextarea, QuestionCategorySelect },
  props: ["goBack"],
  data() {
    return {
      sessionId: new Date().getTime(),
      filePagination: {
        currentPage: 1,
        perPage: 4,
      },
      selectedFile: undefined,
      allowedFormats: ["application/*", "image/*", "text/*"],
      isSubmitting: false,
      reviewDrafts: {},
      reviewDraftMeta: {},
      isSavingReviews: false,
      historyOpen: {},
      relatedPoliciesOpen: {},
    };
  },
  computed: {
    targetUrl() {
      if (!this.questionSet || !this.questionSet._id) {
        return "";
      }
      return `question-set/${this.questionSet._id}/upload`;
    },
    paginatedFileData() {
      if (!this.questionSet || !this.questionSet.files) {
        return [];
      }
      const start = (this.filePagination.currentPage - 1) * this.filePagination.perPage;
      const end = start + this.filePagination.perPage;
      return this.questionSet.files.slice(start, end);
    },
    selectedFileIdInObject() {
      return this.selectedFile ? { selectedFile: this.selectedFile._id } : { selectedFile: undefined };
    },
    questionSet: {
      get() {
        return this.$store.getters["organizationQuestionSet/selectedEntity"];
      },
      set(value) {
        this.$store.commit("organizationQuestionSet/selectedEntity", value);
      },
    },
    isLoading() {
      return this.$store.getters["organizationQuestionSet/isLoading"];
    },
    isFirmQuestionSet() {
      return !!(this.questionSet && this.questionSet.firm);
    },
    isCurrentAccountAssignee() {
      const assignee = this.questionSet ? this.questionSet.assignee : undefined;
      return assignee && this.currentAccount ? assignee._id === this.currentAccount._id : false;
    },

    canAssign() {
      return !this.isFirmQuestionSet;
    },
    canViewHistory() {
      if (this.canReview) {
        return true;
      }
      return this.isFirmQuestionSet;
    },
    timelineItems() {
      if (!this.isFirmQuestionSet || !this.questionSet) {
        return [];
      }
      const items = [
        { key: "audit-start", label: this.$t("firm.audit.audit-start"), value: this.questionSet.auditStartedAt },
        { key: "first-submitted-at", label: this.$t("firm.audit.first-submitted-at"), value: this.questionSet.submittedAt },
        { key: "last-action-at", label: this.$t("firm.audit.last-action-at"), value: this.questionSet.lastStatusChangedAt },
      ];

      if (this.currentAccountHasPermissions(["ORGANIZATION_ADMIN", "ORGANIZATION_INFORMATION_TECHNOLOGY_AUDITOR"])) {
        items.push(
          { key: "test-ready", label: this.$t("firm.audit.test-ready"), value: this.questionSet.testReadyAt },
          { key: "prod-ready", label: this.$t("firm.audit.prod-ready"), value: this.questionSet.prodReadyAt },
          { key: "completed-at", label: this.$t("firm.audit.completed-at"), value: this.questionSet.completedAt },
        );
      }

      return items;
    },
    missingRequiredAttachments() {
      if (!this.questionSet || !this.questionSet.questions) {
        return false;
      }
      return this.questionSet.questions.some((question) => {
        if (question.canDisplay === false) {
          return false;
        }
        const files = question.files || [];
        return question.fileRequired && files.length === 0;
      });
    },
    canSubmitToBt() {
      if (!this.isFirmQuestionSet) {
        return false;
      }
      if (!this.isCurrentAccountAssignee) {
        return false;
      }
      if (this.questionSet.status !== "FIRM_REVIEW") {
        return false;
      }
      if (this.questionSet.questionCount > 0 && this.questionSet.answeredQuestionCount !== this.questionSet.questionCount) {
        return false;
      }
      return !this.missingRequiredAttachments;
    },
    submitWarningText() {
      if (!this.isFirmQuestionSet) {
        return null;
      }
      if (!this.canEditAnswers) {
        return this.$t("firm.audit.submit-warning.readonly", { status: this.currentStatusLabel });
      }
      if (!this.isCurrentAccountAssignee) {
        return this.$t("firm.audit.submit-warning.not-owner");
      }
      if (this.questionSet.questionCount > 0 && this.questionSet.answeredQuestionCount < this.questionSet.questionCount) {
        return this.$t("firm.audit.submit-warning.incomplete");
      }
      if (this.missingRequiredAttachments) {
        return this.$t("firm.audit.submit-warning.missing-attachments");
      }
      return null;
    },
    statusVariant() {
      const map = {
        FIRM_REVIEW: "warning",
        BT_REVIEW: "info",
        COMPLETED: "success",
        IN_PROGRESS: "primary",
        REJECTED: "danger",
        NONE: "secondary",
      };
      const status = this.questionSet ? this.questionSet.status : undefined;
      return map[status] || "secondary";
    },
    currentStatusLabel() {
      const keyMap = {
        FIRM_REVIEW: "firm-review",
        BT_REVIEW: "bt-review",
        COMPLETED: "completed",
        IN_PROGRESS: "in-progress",
        REJECTED: "rejected",
        NONE: "none",
      };
      const status = this.questionSet ? this.questionSet.status : undefined;
      const key = keyMap[status] || "none";
      return this.$t(`firm.audit.status.${key}`);
    },
    firmName() {
      return this.questionSet && this.questionSet.firm ? this.$options.filters.translate(this.questionSet.firm.name) : undefined;
    },
    assigneeName() {
      return this.questionSet && this.questionSet.assignee ? this.questionSet.assignee.name : undefined;
    },
    sourceQuestionSetName() {
      return this.questionSet && this.questionSet.sourceQuestionSet && this.questionSet.sourceQuestionSet.name
        ? this.$options.filters.translate(this.questionSet.sourceQuestionSet.name)
        : undefined;
    },
    canReview() {
      if (!this.isFirmQuestionSet) {
        return false;
      }
      return this.currentAccountHasPermissions(["ORGANIZATION_ADMIN", "ORGANIZATION_INFORMATION_TECHNOLOGY_AUDITOR", "ORGANIZATION_INFORMATION_TECHNOLOGY_ADMIN"]);
    },
    canEditReviews() {
      if (!this.canReview || !this.questionSet) {
        return false;
      }
      return this.questionSet.status === "BT_REVIEW";
    },
    pendingReviewPayload() {
      if (!this.questionSet || !this.questionSet.questions) {
        return [];
      }
      return this.questionSet.questions.reduce((acc, question) => {
        const originalStatus = question.reviewStatus || "PENDING";
        const originalComment = question.reviewComment || "";
        const originalNote = question.reportNote || "";

        const draft = this.reviewDrafts[question._id] || {
          reviewStatus: originalStatus,
          reviewComment: originalComment,
          reportNote: originalNote,
        };

        const status = draft.reviewStatus || "PENDING";
        const comment = draft.reviewComment || "";
        const reportNote = draft.reportNote || "";

        if (status !== originalStatus || comment !== originalComment || reportNote !== originalNote) {
          acc.push({
            questionId: question._id,
            reviewStatus: status,
            reviewComment: ["REVISION_REQUIRED", "PENDING"].includes(status) ? comment : "",
            reportNote,
          });
        }

        return acc;
      }, []);
    },
    hasReviewChanges() {
      return this.pendingReviewPayload.length > 0;
    },
    unansweredQuestionOrders() {
      if (!this.questionSet || !this.questionSet.questions) {
        return [];
      }
      return this.questionSet.questions
        .filter((q) => {
          if (q.canDisplay === false) return false;

          const hasAnswer = q.answerType === "FREE_TEXT" ? q.freeTextAnswer && q.freeTextAnswer.trim().length > 0 : q.answers && q.answers.length > 0;

          const hasRequiredFiles = !q.fileRequired || (q.files && q.files.length > 0);

          return !hasAnswer || !hasRequiredFiles;
        })
        .map((q) => q.order);
    },
  },
  mounted() {
    this.fetchDetail();
  },
  watch: {
    questionSet: {
      immediate: true,
      deep: true,
      handler(newValue) {
        this.syncReviewDrafts(newValue);
      },
    },
  },
  methods: {
    canEditAnswers(question) {
      if (!this.isFirmQuestionSet) {
        return true;
      }
      return this.questionSet.status === "FIRM_REVIEW" && this.isCurrentAccountAssignee && question.reviewStatus != "APPROVED";
    },
    async fetchDetail() {
      try {
        const id = this.$route.params.id;
        await this.$store.dispatch("organizationQuestionSet/findById", id);
        this.historyOpen = {};
      } catch (error) {
        if (error.status == 403) {
          setTimeout(() => this.goBack(), 1000);
        }
      }
    },
    async handleUploadResponse(question, event) {
      if (this.isUploading) return;

      if (event && event.status !== 200) {
        this.$handleHttpErrorByStatusCode(event.error);
        return;
      }

      // Save current scroll position to prevent page jump
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      const uploadedFile = event?.data;
      if (uploadedFile && question && question._id) {
        this.questionSet = {
          ...this.questionSet,
          questions: this.questionSet.questions.map((q) => {
            if (q._id === question._id) {
              const files = Array.isArray(q.files) ? [...q.files, uploadedFile] : [uploadedFile];
              return { ...q, files };
            }
            return q;
          }),
        };
      }

      if (question.answerType == "FREE_TEXT" && question.fileRequired && (question.freeTextAnswer || "").trim().length == 0) {
        // Get the updated question object that includes the newly uploaded file
        const updatedQuestion = this.questionSet.questions.find((q) => q._id === question._id) || question;

        updatedQuestion.freeTextAnswer = "Yanıt Ektedir";

        this.questionSet = {
          ...this.questionSet,
          questions: this.questionSet.questions.map((q) => {
            if (q._id == question._id) {
              return updatedQuestion;
            }
            return q;
          }),
        };

        // Use the updated question object that has the files array
        await this.updateAnswer({ target: { value: "Yanıt Ektedir" } }, updatedQuestion);
      }

      // Restore scroll position after Vue finishes rendering
      this.$nextTick(() => {
        window.scrollTo(0, scrollPosition);
      });
    },
    onQuestionUpload(question, event) {
      this.handleUploadResponse(question, event);
    },
    downloadFile(file) {
      let isApproval = false;
      if (file.approval && file.approval.path && file.approval.path != file.path) {
        isApproval = true;
      }
      this.$store.commit("fileManager/addDownloadedFile", { file, isApproval });
    },
    async deleteFile(question, item) {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch("fileManager/delete", item._id);
        this.questionSet = {
          ...this.questionSet,
          questions: this.questionSet.questions.map((q) => {
            if (q._id === question._id) {
              return { ...q, files: (q.files || []).filter((f) => f._id !== item._id) };
            }
            return q;
          }),
        };

        this.$toast.success(`${this.$t("common.delete-successful")} [${this.$options.filters.translate(item.name)}]`);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error, { customMessage: `${this.$t("common.delete-fail")} [${this.$options.filters.translate(item.name)}]` });
      }
    },
    async selectOption(question, option) {
      if (!this.canEditAnswers(question)) {
        return;
      }
      const payload = JSON.parse(JSON.stringify({ question, option, sessionId: this.sessionId }));

      try {
        await this.$store.dispatch("organizationQuestionSet/markAnswer", payload);

        this.$toast.success(this.$t("question.answer-success"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    isOptionSelected(question, option) {
      return !!question.answers.find((i) => i.optionId == option._id);
    },

    async onAssigneeChanged(account, questionSet) {
      const newAssigneeId = account ? account._id : undefined;
      const currentAssigneeId = questionSet && questionSet.assignee ? questionSet.assignee._id : undefined;
      if (newAssigneeId && newAssigneeId === currentAssigneeId) {
        return;
      }
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }

      try {
        await this.$store.dispatch("organizationQuestionSet/assign", { account, questionSet });

        this.$toast.success(this.$t("common.success"));
        if (account && account._id != this.currentAccount._id) {
          this.goBack();
        }
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    updateNote($e, question) {
      if (!this.canEditAnswers(question)) {
        return;
      }
      if (!question || !question._id) {
        return;
      }
      this.debounce(
        async () => {
          try {
            const note = $e.target.value;
            await this.$store.dispatch("organizationQuestionSet/updateNote", {
              question,
              note,
              sessionId: this.sessionId,
            });
            this.$toast.success(this.$t("question.note-saved"));
          } catch (error) {
            this.$handleHttpErrorByStatusCode(error);
          }
        },
        question._id,
        1500,
      );
    },

    async updateAnswer($e, question) {
      return new Promise((resolve, reject) => {
        if (!this.canEditAnswers(question)) {
          return;
        }
        this.debounce(
          async () => {
            try {
              let freeTextAnswer = $e.target.value;
              if (question.answerType == "FREE_TEXT" && question.fileRequired && question.files.length > 0) {
                if ((freeTextAnswer || "").trim().length == 0) {
                  freeTextAnswer = "Yanıt Ektedir";
                }
              }

              const payload = JSON.parse(JSON.stringify({ question, freeTextAnswer, sessionId: this.sessionId }));
              await this.$store.dispatch("organizationQuestionSet/markAnswer", payload);
              this.$toast.success(this.$t("question.answer-success"));
              resolve();
            } catch (error) {
              reject();
              this.$handleHttpErrorByStatusCode(error);
            }
          },
          question._id,
          1500,
        );
      });
    },
    reviewStatusLabel(status) {
      const keyMap = {
        APPROVED: "approved",
        REVISION_REQUIRED: "revision",
        PENDING: "pending",
        NONE: "none",
      };
      const key = keyMap[status] || "none";
      return this.$t(`firm.audit.review.status.${key}`);
    },
    reviewStatusVariant(status) {
      const map = {
        APPROVED: "success",
        REVISION_REQUIRED: "danger",
        PENDING: "warning",
        NONE: "secondary",
      };
      return map[status] || "secondary";
    },
    reviewDraft(question) {
      if (!question || !question._id) {
        return { reviewStatus: "PENDING", reviewComment: "", reportNote: "" };
      }
      if (!this.reviewDrafts[question._id]) {
        this.$set(this.reviewDrafts, question._id, {
          reviewStatus: question.reviewStatus || "PENDING",
          reviewComment: question.reviewComment || "",
          reportNote: question.reportNote || "",
        });
        if (!this.reviewDraftMeta[question._id]) {
          this.$set(this.reviewDraftMeta, question._id, { touched: false });
        }
      }
      return this.reviewDrafts[question._id];
    },
    setReviewStatus(question, status) {
      if (!this.canEditReviews) {
        return;
      }
      const draft = this.reviewDraft(question);
      const initialStatus = question.reviewStatus || "PENDING";
      if (initialStatus === "APPROVED" && status !== "APPROVED") {
        const meta = this.reviewDraftMeta[question._id] || { touched: false };
        this.$set(this.reviewDraftMeta, question._id, { ...meta, touched: true });
      }
      this.$set(this.reviewDrafts, question._id, { ...draft, reviewStatus: status });
    },
    isApproveDisabled(question) {
      if (!question || !question._id) {
        return false;
      }
      const initialStatus = question.reviewStatus || "PENDING";
      if (initialStatus !== "APPROVED") {
        return false;
      }
      const meta = this.reviewDraftMeta[question._id];
      const touched = meta?.touched || false;
      const currentStatus = this.reviewDraft(question).reviewStatus;
      return !touched && currentStatus === "APPROVED";
    },
    syncReviewDrafts(questionSet) {
      if (!questionSet || !questionSet.questions) {
        this.reviewDrafts = {};
        this.reviewDraftMeta = {};
        this.historyOpen = {};
        return;
      }
      const drafts = {};
      const meta = {};
      questionSet.questions.forEach((question) => {
        drafts[question._id] = {
          reviewStatus: question.reviewStatus || "PENDING",
          reviewComment: question.reviewComment || "",
          reportNote: question.reportNote || "",
        };
        meta[question._id] = { touched: false };
      });
      this.reviewDrafts = drafts;
      this.reviewDraftMeta = meta;
      this.historyOpen = {};
    },
    async submitForBt() {
      if (!this.canSubmitToBt || this.isSubmitting) {
        return;
      }
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      this.isSubmitting = true;
      try {
        await this.$store.dispatch("organizationQuestionSet/submitForBt", this.questionSet._id);
        this.$toast.success(this.$t("firm.audit.submit-success"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.isSubmitting = false;
      }
    },
    async saveAllReviews() {
      if (!this.canEditReviews || !this.questionSet || !this.questionSet._id) {
        return;
      }
      const payload = this.pendingReviewPayload;
      if (!payload.length) {
        return;
      }
      const requiresComment = payload.find((item) => item.reviewStatus === "REVISION_REQUIRED" && !item.reviewComment);
      if (requiresComment) {
        this.$toast.error(this.$t("firm.audit.review.required-comment"));
        return;
      }

      this.isSavingReviews = true;
      try {
        await this.$store.dispatch("organizationQuestionSet/reviewQuestionsBulk", {
          questionSetId: this.questionSet._id,
          items: payload,
        });
        this.$toast.success(this.$t("firm.audit.review.saved"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.isSavingReviews = false;
      }
    },
    toggleHistory(question, type) {
      if (!question || !question._id) {
        return;
      }
      const key = `${question._id}-${type}`;
      this.$set(this.historyOpen, key, !this.historyOpen[key]);
    },
    toggleRelatedPolicies(question, type) {
      if (!question || !question._id) {
        return;
      }
      const key = `${question._id}-${type}`;
      this.$set(this.relatedPoliciesOpen, key, !this.relatedPoliciesOpen[key]);
    },
    isHistoryOpen(question, type) {
      if (!question || !question._id) {
        return false;
      }

      return !!this.historyOpen[`${question._id}-${type}`];
    },
    isRelatedPoliciesOpen(question, type) {
      if (!question || !question._id) {
        return false;
      }

      return !!this.relatedPoliciesOpen[`${question._id}-${type}`];
    },
    scrollToQuestion(order) {
      const el = document.getElementById(`question-${order}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./question-set";

.question {
  align-items: flex-start;
  flex-wrap: wrap;
}

.question .text {
  word-break: break-word;
}

.question-body {
  flex: 1 1 0;
  min-width: 0;
}

.question textarea {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.top-meta {
  gap: 1.5rem;
}

.status-pill {
  min-width: 220px;
}

.status-pill .badge {
  font-size: 0.85rem;
  padding: 0.4rem 0.75rem;
}

.timeline-container {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  background: #f6f9fc;
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.timeline-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));

  align-items: center;
  text-align: center;
}

.timeline-cell {
  padding: 0.25rem 0.35rem;
  min-width: 0;
}

.timeline-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6c757d;
  font-weight: 600;
}

.answers.disabled,
.answer.disabled,
textarea:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.bt-comment {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  line-height: 1.3rem;
}

.review-panel {
  background: #f6f9fc;
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.review-panel .btn-group .btn {
  min-width: 120px;
}

.review-panel .btn + .btn {
  margin-left: 0;
}

.history-wrapper {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-top: 1rem;
}

.history-table th,
.history-table td {
  vertical-align: top;
  font-size: 0.85rem;
}

.question-files {
  background: #f8f9fc;
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.question-file-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.75rem;
}

.question-file-card {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 120px;
  overflow: hidden;
}

.question-file-card .font-weight-600 {
  font-weight: 600;
  word-break: break-word;
  overflow-wrap: break-word;
}

.question-file-actions i {
  font-size: 1.1rem;
}

.unanswered-bar {
  background: #fffcf0;
  border: 1px solid #ffe69c;
  border-radius: 12px;
  padding: 1rem 1.25rem;
}

.unanswered-label {
  font-weight: 600;
  color: #856404;
  font-size: 0.95rem;
}

.unanswered-badge {
  background-color: #fee2e2;
  color: #ef4444;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  transition: all 0.2s;
  min-width: 28px;
  text-align: center;

  &:hover {
    background-color: #fecaca;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  }
}

.question-sidebar {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.back-to-top-link {
  color: #c3cce7;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;

  &:hover {
    color: #ff7c48;
    background: #ffeee8;
    text-decoration: none;
  }
}
</style>
